import { Box, Flex, Text, Collapse, Spinner } from "@chakra-ui/react";
import { useState, useRef, useEffect } from "react";
import colors from "core/resources/theme/colors";
import { BiChevronDown, BiChevronRight } from "react-icons/bi";
import TagRowCobranca from "./components/TagRowCobranca";
import TableItemCobranca from "../TableItemCobranca/index";
import { CobrancaContaDetailsItem } from "features/cobranca/typings";
import { Checkbox } from "antd";
import { formatMoney } from "infra/helpers/number";
import useDetalhesContaTaxasAtualizadas from "core/features/cobranca/hooks/useDetalhesContaTaxasAtualizadas";

export interface RowData {
  primeiroItem: boolean;
  ultimoItem: boolean;
}

export interface ItemCobrancaProps {
  unidadeId: string;
  conta: CobrancaContaDetailsItem;
  dataPosicao: string;
  rowData: RowData;
  selectable?: boolean;
  selected: string[];
  setSelected: (selected: string[]) => any;
  taxasModificadas?: Record<
    string,
    {
      taxaMulta: number;
      taxaJuros: number;
      taxaCobranca: number;
    }
  >;
  onChangeTaxas?: ({
    contaId,
    categoriaFinanceiraId,
    taxas,
  }: {
    contaId: string;
    categoriaFinanceiraId: string;
    taxas: { taxaMulta: number; taxaJuros: number; taxaCobranca: number };
  }) => any;
  onContaModificada?: (conta: CobrancaContaDetailsItem) => any;
}

function ItemCobranca({
  unidadeId,
  conta,
  dataPosicao,
  selected,
  selectable = true,
  setSelected,
  rowData,
  taxasModificadas,
  onChangeTaxas,
  onContaModificada,
}: ItemCobrancaProps) {
  const { primeiroItem, ultimoItem } = rowData;
  const [isOpen, setIsOpen] = useState(primeiroItem ? true : false);
  const {
    data: contaAtualizada,
    isLoading,
    isFetching,
  } = useDetalhesContaTaxasAtualizadas({
    unidadeId,
    contaId: conta.id,
    dataPosicao,
    taxasModificadas,
  });

  const cacheConta = useRef(contaAtualizada);

  const toggle = () => setIsOpen((state) => !state);

  const onSelect = () => {
    setSelected([...selected, conta.id]);
  };

  const onUnselect = () => {
    setSelected(selected.filter((id) => id !== conta.id));
  };

  const isSelected = selected.includes(conta.id);

  const onCheckAllChange = isSelected ? onUnselect : onSelect;

  const _conta = contaAtualizada || cacheConta.current || conta;

  useEffect(() => {
    if (contaAtualizada) {
      cacheConta.current = contaAtualizada;
      onContaModificada?.(contaAtualizada);
    }
  }, [contaAtualizada, onContaModificada]);

  return (
    <Box>
      <Flex
        cursor="pointer"
        bg={colors.lightBlue}
        onClick={toggle}
        py={1}
        pr={6}
        pl={2}
        alignItems="center"
        borderBottom={`1px solid ${colors.gray}`}
        roundedTop={primeiroItem ? 16 : 0}
        roundedBottom={ultimoItem && !primeiroItem ? 16 : 0}
      >
        <Flex flex={0.1} pl={4}>
          <Checkbox
            onChange={selectable ? onCheckAllChange : undefined}
            checked={isSelected}
            disabled={!selectable}
            onClick={(ev) => ev.stopPropagation()}
          />
        </Flex>
        <Flex alignItems="center" h="40px">
          {isOpen ? (
            <BiChevronDown size={25} color={colors.black700} />
          ) : (
            <BiChevronRight size={25} color={colors.black700} />
          )}
        </Flex>
        <Flex w="100%" gap={6}>
          <Text ml={1} fontSize="16px" color={colors.black1000}>
            {_conta.descricao}
          </Text>
          <Flex gap={4}>
            <TagRowCobranca
              dataPosicao={dataPosicao}
              dataVencimento={_conta.dataVencimento}
              diasEmAtraso={_conta.diasEmAtraso}
              origemContaId={_conta.origemContaId}
              semIndiceMesDataPosicao={_conta.semIndiceMesDataPosicao}
            />
          </Flex>
        </Flex>
        <Text ml={"auto"} fontWeight="bold">
          {formatMoney(_conta.total)}
        </Text>
      </Flex>
      <Collapse in={isOpen}>
        <Box position="relative">
          {isLoading || isFetching ? (
            <Flex
              position="absolute"
              w="100%"
              h="100%"
              alignItems="center"
              justify="center"
              bg="#FFFFFFAA"
            >
              <Spinner size="md" />
            </Flex>
          ) : null}

          <TableItemCobranca
            item={{ ..._conta }}
            onChangeTaxas={onChangeTaxas}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

export default ItemCobranca;
